import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Send, CheckCircle, AlertCircle, Plus, Trash2, Users, Settings,
  ToggleLeft, ToggleRight, Clock, Filter, RefreshCw, ChevronLeft, ChevronRight,
  Shield, Bell, FileText, Zap, Activity, XCircle, Search
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALERT_ICONS = {
  MRO_APPROVAL: <Zap className="w-4 h-4" />,
  QUALITY_REJECTION: <Shield className="w-4 h-4" />,
  LOW_INVENTORY: <AlertCircle className="w-4 h-4" />,
  SHIPMENT_DELAY: <Clock className="w-4 h-4" />,
  DAILY_SUMMARY: <FileText className="w-4 h-4" />,
};

const ALERT_COLORS = {
  MRO_APPROVAL: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  QUALITY_REJECTION: 'bg-red-50 border-red-200 text-red-700',
  LOW_INVENTORY: 'bg-amber-50 border-amber-200 text-amber-700',
  SHIPMENT_DELAY: 'bg-orange-50 border-orange-200 text-orange-700',
  DAILY_SUMMARY: 'bg-blue-50 border-blue-200 text-blue-700',
};

const STATUS_COLORS = {
  SUCCESS: 'bg-emerald-100 text-emerald-800',
  FAIL: 'bg-red-100 text-red-800',
  DISABLED: 'bg-slate-100 text-slate-600',
};

// ===== Sub-components =====

const RulesTab = ({ rules, users, onToggleRule, onUpdateRule, onTestRule }) => {
  const [editingRule, setEditingRule] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testingRuleId, setTestingRuleId] = useState(null);

  const handleTest = async (rule) => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Enter a valid email to send test');
      return;
    }
    setTestingRuleId(rule.id);
    await onTestRule(rule.id, testEmail);
    setTestingRuleId(null);
  };

  const handleSaveRecipients = async (rule) => {
    await onUpdateRule(rule.id, {
      to_user_ids: rule.to_user_ids,
      cc_user_ids: rule.cc_user_ids,
    });
    setEditingRule(null);
  };

  const toggleUserInList = (rule, field, userId) => {
    const list = rule[field] || [];
    const updated = list.includes(userId)
      ? list.filter(id => id !== userId)
      : [...list, userId];
    rule[field] = updated;
  };

  return (
    <div className="space-y-4">
      {/* Test email input for all rules */}
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
        <Label className="text-sm font-medium whitespace-nowrap">Test Email:</Label>
        <Input
          type="email"
          placeholder="your-email@example.com"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          className="max-w-xs"
          data-testid="rules-test-email-input"
        />
        <span className="text-xs text-slate-500">Used for "Send Test" on each rule</span>
      </div>

      {rules.map((rule) => (
        <Card
          key={rule.id}
          className={`border transition-all ${rule.is_enabled ? 'border-slate-200' : 'border-slate-100 opacity-70'}`}
          data-testid={`email-rule-card-${rule.alert_type}`}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg border ${ALERT_COLORS[rule.alert_type] || 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                  {ALERT_ICONS[rule.alert_type] || <Bell className="w-4 h-4" />}
                </div>
                <div>
                  <CardTitle className="text-base">{rule.alert_name}</CardTitle>
                  <CardDescription className="text-xs mt-0.5">{rule.description}</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTest(rule)}
                  disabled={testingRuleId === rule.id || !testEmail}
                  data-testid={`test-rule-btn-${rule.alert_type}`}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {testingRuleId === rule.id ? 'Sending...' : 'Send Test'}
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{rule.is_enabled ? 'Active' : 'Disabled'}</span>
                  <Switch
                    checked={rule.is_enabled}
                    onCheckedChange={(val) => onToggleRule(rule.id, val)}
                    data-testid={`toggle-rule-${rule.alert_type}`}
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">Default To: <strong className="text-slate-700">{rule.default_recipient || 'N/A'}</strong></span>
                <span className="text-slate-400">|</span>
                <span className="text-slate-500">
                  Extra TO: <strong className="text-slate-700">{(rule.to_user_ids || []).length} user(s)</strong>
                </span>
                <span className="text-slate-400">|</span>
                <span className="text-slate-500">
                  CC: <strong className="text-slate-700">{(rule.cc_user_ids || []).length} user(s)</strong>
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingRule(editingRule === rule.id ? null : rule.id)}
                data-testid={`edit-recipients-btn-${rule.alert_type}`}
              >
                <Users className="w-3.5 h-3.5 mr-1.5" />
                {editingRule === rule.id ? 'Close' : 'Edit Recipients'}
              </Button>
            </div>

            {editingRule === rule.id && (
              <RecipientEditor
                rule={rule}
                users={users}
                onToggleUser={toggleUserInList}
                onSave={() => handleSaveRecipients(rule)}
              />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const RecipientEditor = ({ rule, users, onToggleUser, onSave }) => {
  const [localTo, setLocalTo] = useState([...(rule.to_user_ids || [])]);
  const [localCc, setLocalCc] = useState([...(rule.cc_user_ids || [])]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = users.filter(u =>
    u.email && (
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleToggle = (field, userId) => {
    if (field === 'to_user_ids') {
      setLocalTo(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    } else {
      setLocalCc(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    }
  };

  const handleSave = () => {
    rule.to_user_ids = localTo;
    rule.cc_user_ids = localCc;
    onSave();
  };

  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-lg border space-y-3" data-testid={`recipient-editor-${rule.alert_type}`}>
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs h-8 text-sm"
          data-testid={`search-users-${rule.alert_type}`}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium mb-2 block">Extra TO Recipients</Label>
          <div className="space-y-1.5 max-h-40 overflow-y-auto border rounded p-2 bg-white">
            {filteredUsers.length === 0 ? (
              <p className="text-xs text-slate-400 p-2">No users with email found</p>
            ) : filteredUsers.map(user => (
              <label key={user.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={localTo.includes(user.id)}
                  onChange={() => handleToggle('to_user_ids', user.id)}
                  className="rounded border-slate-300"
                />
                <span className="truncate">{user.username}</span>
                <span className="text-xs text-slate-400 truncate">({user.email})</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm font-medium mb-2 block">CC Recipients</Label>
          <div className="space-y-1.5 max-h-40 overflow-y-auto border rounded p-2 bg-white">
            {filteredUsers.length === 0 ? (
              <p className="text-xs text-slate-400 p-2">No users with email found</p>
            ) : filteredUsers.map(user => (
              <label key={user.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={localCc.includes(user.id)}
                  onChange={() => handleToggle('cc_user_ids', user.id)}
                  className="rounded border-slate-300"
                />
                <span className="truncate">{user.username}</span>
                <span className="text-xs text-slate-400 truncate">({user.email})</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} data-testid={`save-recipients-btn-${rule.alert_type}`}>
          Save Recipients
        </Button>
      </div>
    </div>
  );
};


const LogsTab = ({ logs, logsData, logsPage, setLogsPage, logsFilter, setLogsFilter, onRefresh, loading }) => {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={logsFilter.alert_type} onValueChange={(v) => setLogsFilter(prev => ({ ...prev, alert_type: v }))}>
          <SelectTrigger className="w-[180px]" data-testid="logs-filter-alert-type">
            <SelectValue placeholder="All Alert Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Alert Types</SelectItem>
            <SelectItem value="MRO_APPROVAL">MRO Approval</SelectItem>
            <SelectItem value="QUALITY_REJECTION">Quality Rejection</SelectItem>
            <SelectItem value="LOW_INVENTORY">Low Inventory</SelectItem>
            <SelectItem value="SHIPMENT_DELAY">Shipment Delay</SelectItem>
            <SelectItem value="DAILY_SUMMARY">Daily Summary</SelectItem>
            <SelectItem value="CUSTOM">Custom</SelectItem>
            <SelectItem value="TEST">Test</SelectItem>
          </SelectContent>
        </Select>

        <Select value={logsFilter.status} onValueChange={(v) => setLogsFilter(prev => ({ ...prev, status: v }))}>
          <SelectTrigger className="w-[140px]" data-testid="logs-filter-status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="SUCCESS">Success</SelectItem>
            <SelectItem value="FAIL">Failed</SelectItem>
            <SelectItem value="DISABLED">Disabled</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-logs-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-logs-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-3 font-medium">Timestamp</th>
                  <th className="text-left p-3 font-medium">Alert Type</th>
                  <th className="text-left p-3 font-medium">Recipients</th>
                  <th className="text-left p-3 font-medium">Subject</th>
                  <th className="text-left p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-slate-400">
                      No email logs found. Logs will appear here when emails are sent.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-slate-50/50 transition-colors">
                      <td className="p-3 whitespace-nowrap text-xs text-slate-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className={`text-xs ${ALERT_COLORS[log.alert_type] || ''}`}>
                          {log.is_test && <span className="mr-1">[TEST]</span>}
                          {log.alert_type}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs max-w-[200px] truncate" title={(log.recipients_to || []).join(', ')}>
                        {(log.recipients_to || []).join(', ') || '-'}
                        {log.recipients_cc?.length > 0 && (
                          <span className="text-slate-400 block">CC: {log.recipients_cc.join(', ')}</span>
                        )}
                      </td>
                      <td className="p-3 text-xs max-w-[250px] truncate" title={log.subject}>
                        {log.subject}
                      </td>
                      <td className="p-3">
                        <Badge className={`text-xs ${STATUS_COLORS[log.status] || 'bg-slate-100'}`}>
                          {log.status === 'SUCCESS' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {log.status === 'FAIL' && <XCircle className="w-3 h-3 mr-1" />}
                          {log.status}
                        </Badge>
                        {log.error_message && (
                          <p className="text-[10px] text-red-500 mt-1 truncate max-w-[200px]" title={log.error_message}>
                            {log.error_message}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {logsData.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">
            Page {logsData.page} of {logsData.total_pages} ({logsData.total} total logs)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={logsPage <= 1}
              onClick={() => setLogsPage(p => p - 1)}
              data-testid="logs-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={logsPage >= logsData.total_pages}
              onClick={() => setLogsPage(p => p + 1)}
              data-testid="logs-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


const CustomAlertsTab = ({ users }) => {
  const [alerts, setAlerts] = useState([]);
  const [eventTypes, setEventTypes] = useState({});
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testingId, setTestingId] = useState(null);

  const emptyForm = { name: '', description: '', event_type: '', conditions: [{ field: '', operator: '>=', value: '' }], logic: 'AND', to_user_ids: [], cc_user_ids: [], to_emails: [''] };
  const [form, setForm] = useState({ ...emptyForm });

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const [alertsRes, typesRes] = await Promise.all([
        axios.get(`${API}/custom-alerts`),
        axios.get(`${API}/custom-alerts/event-types`)
      ]);
      setAlerts(alertsRes.data || []);
      setEventTypes(typesRes.data.event_types || {});
      setOperators(typesRes.data.operators || []);
    } catch (error) {
      toast.error('Failed to load custom alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const availableFields = form.event_type ? (eventTypes[form.event_type]?.fields || []) : [];

  const handleAddCondition = () => {
    setForm(prev => ({ ...prev, conditions: [...prev.conditions, { field: '', operator: '>=', value: '' }] }));
  };

  const handleRemoveCondition = (idx) => {
    setForm(prev => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== idx) }));
  };

  const handleConditionChange = (idx, key, val) => {
    setForm(prev => {
      const conds = [...prev.conditions];
      conds[idx] = { ...conds[idx], [key]: val };
      return { ...prev, conditions: conds };
    });
  };

  const handleAddEmail = () => {
    setForm(prev => ({ ...prev, to_emails: [...prev.to_emails, ''] }));
  };

  const handleEmailChange = (idx, val) => {
    setForm(prev => {
      const emails = [...prev.to_emails];
      emails[idx] = val;
      return { ...prev, to_emails: emails };
    });
  };

  const handleRemoveEmail = (idx) => {
    setForm(prev => ({ ...prev, to_emails: prev.to_emails.filter((_, i) => i !== idx) }));
  };

  const handleToggleUser = (field, userId) => {
    setForm(prev => {
      const list = prev[field] || [];
      return { ...prev, [field]: list.includes(userId) ? list.filter(id => id !== userId) : [...list, userId] };
    });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.event_type || !form.conditions.length) {
      toast.error('Name, event type, and at least one condition are required');
      return;
    }
    const validConds = form.conditions.filter(c => c.field && c.operator && c.value !== '');
    if (!validConds.length) { toast.error('Complete at least one condition'); return; }

    const validEmails = form.to_emails.filter(e => e && e.includes('@'));
    if (!form.to_user_ids.length && !validEmails.length) {
      toast.error('Add at least one recipient (user or email)');
      return;
    }

    const payload = { ...form, conditions: validConds.map(c => ({ ...c, value: parseFloat(c.value) })), to_emails: validEmails };

    try {
      if (editingId) {
        await axios.put(`${API}/custom-alerts/${editingId}`, payload);
        toast.success('Custom alert updated');
      } else {
        await axios.post(`${API}/custom-alerts`, payload);
        toast.success('Custom alert created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyForm });
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save alert');
    }
  };

  const handleEdit = (alert) => {
    setForm({
      name: alert.name,
      description: alert.description || '',
      event_type: alert.event_type,
      conditions: alert.conditions.length ? alert.conditions.map(c => ({ ...c, value: String(c.value) })) : [{ field: '', operator: '>=', value: '' }],
      logic: alert.logic || 'AND',
      to_user_ids: alert.to_user_ids || [],
      cc_user_ids: alert.cc_user_ids || [],
      to_emails: alert.to_emails?.length ? alert.to_emails : [''],
    });
    setEditingId(alert.id);
    setShowForm(true);
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm('Delete this custom alert?')) return;
    try {
      await axios.delete(`${API}/custom-alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleToggle = async (alertId, enabled) => {
    try {
      await axios.put(`${API}/custom-alerts/${alertId}`, { is_enabled: enabled });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_enabled: enabled } : a));
      toast.success(`Alert ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const handleTest = async (alertId) => {
    if (!testEmail || !testEmail.includes('@')) { toast.error('Enter a valid test email first'); return; }
    setTestingId(alertId);
    try {
      const res = await axios.post(`${API}/custom-alerts/test/${alertId}`, { test_email: testEmail });
      if (res.data.status === 'success') toast.success('Test email sent!');
      else if (res.data.status === 'disabled') toast.warning('Email service not configured');
      else toast.error('Test failed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Label className="text-sm font-medium whitespace-nowrap">Test Email:</Label>
          <Input type="email" placeholder="test@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="max-w-xs" data-testid="custom-alerts-test-email" />
        </div>
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ ...emptyForm }); }} data-testid="create-custom-alert-btn">
          <Plus className="w-4 h-4 mr-1.5" />{showForm && !editingId ? 'Cancel' : 'Create Custom Alert'}
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="border-2 border-indigo-200" data-testid="custom-alert-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{editingId ? 'Edit' : 'Create'} Custom Alert</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Alert Name *</Label>
                <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. High Rejection Alert" className="mt-1" data-testid="alert-name-input" />
              </div>
              <div>
                <Label className="text-sm">Event Type *</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm(prev => ({ ...prev, event_type: v, conditions: [{ field: '', operator: '>=', value: '' }] }))}>
                  <SelectTrigger className="mt-1" data-testid="event-type-select">
                    <SelectValue placeholder="Select event..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(eventTypes).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-sm">Description</Label>
              <Input value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="When should this alert fire?" className="mt-1" data-testid="alert-description-input" />
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Conditions *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Logic:</span>
                  <Select value={form.logic} onValueChange={(v) => setForm(prev => ({ ...prev, logic: v }))}>
                    <SelectTrigger className="w-20 h-7 text-xs" data-testid="logic-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AND">AND</SelectItem>
                      <SelectItem value="OR">OR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                {form.conditions.map((cond, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Select value={cond.field} onValueChange={(v) => handleConditionChange(idx, 'field', v)} disabled={!form.event_type}>
                      <SelectTrigger className="w-[180px]" data-testid={`condition-field-${idx}`}>
                        <SelectValue placeholder="Field..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFields.map(f => (
                          <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={cond.operator} onValueChange={(v) => handleConditionChange(idx, 'operator', v)}>
                      <SelectTrigger className="w-[140px]" data-testid={`condition-operator-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map(op => (
                          <SelectItem key={op.key} value={op.key}>{op.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Input type="number" value={cond.value} onChange={(e) => handleConditionChange(idx, 'value', e.target.value)} placeholder="Value" className="w-24" data-testid={`condition-value-${idx}`} />

                    {form.conditions.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveCondition(idx)} className="text-red-500 hover:text-red-700 px-2">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={handleAddCondition} className="mt-2" data-testid="add-condition-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Add Condition
              </Button>
            </div>

            {/* Recipients - Email addresses */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Email Recipients</Label>
              <div className="space-y-2">
                {form.to_emails.map((email, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input type="email" value={email} onChange={(e) => handleEmailChange(idx, e.target.value)} placeholder="recipient@example.com" className="flex-1" data-testid={`to-email-${idx}`} />
                    {form.to_emails.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveEmail(idx)} className="text-red-500 px-2">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={handleAddEmail} data-testid="add-email-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Email
                </Button>
              </div>
            </div>

            {/* Recipients - Users */}
            {users.filter(u => u.email).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">TO Users</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto border rounded p-2 bg-white">
                    {users.filter(u => u.email).map(user => (
                      <label key={user.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                        <input type="checkbox" checked={form.to_user_ids.includes(user.id)} onChange={() => handleToggleUser('to_user_ids', user.id)} className="rounded" />
                        <span className="truncate">{user.username}</span>
                        <span className="text-xs text-slate-400">({user.email})</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">CC Users</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto border rounded p-2 bg-white">
                    {users.filter(u => u.email).map(user => (
                      <label key={user.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 rounded cursor-pointer">
                        <input type="checkbox" checked={form.cc_user_ids.includes(user.id)} onChange={() => handleToggleUser('cc_user_ids', user.id)} className="rounded" />
                        <span className="truncate">{user.username}</span>
                        <span className="text-xs text-slate-400">({user.email})</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
              <Button onClick={handleSubmit} data-testid="save-custom-alert-btn">
                {editingId ? 'Update Alert' : 'Create Alert'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Custom Alerts List */}
      {loading ? (
        <p className="text-sm text-slate-400 text-center py-8">Loading...</p>
      ) : alerts.length === 0 && !showForm ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No custom alerts yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first custom alert to get notified based on your conditions</p>
          </CardContent>
        </Card>
      ) : (
        alerts.map(alert => (
          <Card key={alert.id} className={`border transition-all ${alert.is_enabled ? '' : 'opacity-60'}`} data-testid={`custom-alert-card-${alert.id}`}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm">{alert.name}</h4>
                    <Badge variant="outline" className="text-[10px]">{eventTypes[alert.event_type]?.name || alert.event_type}</Badge>
                  </div>
                  {alert.description && <p className="text-xs text-slate-500 mb-2">{alert.description}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {(alert.conditions || []).map((c, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px] font-mono">
                        {c.field} {c.operator} {c.value}
                      </Badge>
                    ))}
                    {alert.conditions?.length > 1 && (
                      <Badge variant="outline" className="text-[10px]">Logic: {alert.logic}</Badge>
                    )}
                  </div>
                  <div className="mt-2 text-[11px] text-slate-400">
                    TO: {[...(alert.to_emails || []), ...(alert.to_user_ids || []).map(id => users.find(u => u.id === id)?.email || id)].filter(Boolean).join(', ') || 'None'}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => handleTest(alert.id)} disabled={testingId === alert.id || !testEmail} className="text-xs">
                    <Send className="w-3 h-3 mr-1" />{testingId === alert.id ? '...' : 'Test'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(alert)} className="text-xs">Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(alert.id)} className="text-red-500 hover:text-red-700 text-xs">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                  <Switch checked={alert.is_enabled} onCheckedChange={(v) => handleToggle(alert.id, v)} data-testid={`toggle-custom-${alert.id}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};


const SettingsTab = () => {
  const [customEmail, setCustomEmail] = useState({ recipient: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSendCustomEmail = async () => {
    if (!customEmail.recipient || !customEmail.subject || !customEmail.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setSending(true);
    try {
      const response = await axios.post(`${API}/email/send-custom`, customEmail);
      if (response.data.status === 'success') {
        toast.success('Email sent successfully!');
        setCustomEmail({ recipient: '', subject: '', message: '' });
      } else if (response.data.status === 'disabled') {
        toast.warning('Email service not configured');
      }
    } catch (error) {
      toast.error('Failed to send email', { description: error.response?.data?.detail || error.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Gmail Setup */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="w-4 h-4 text-slate-600" />
            Gmail SMTP Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="font-semibold text-amber-900 mb-2 text-sm">Setup Instructions</h3>
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-amber-800">
              <li>Enable 2-Step Verification on your Google account</li>
              <li>Generate an App Password at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">myaccount.google.com/apppasswords</a></li>
              <li>Add credentials to <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">/app/backend/.env</code></li>
              <li>Restart backend after updating</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Send Custom Email */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4 text-indigo-600" />
            Send Custom Email
          </CardTitle>
          <CardDescription>Send a manual one-off email notification</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <Label htmlFor="recipient" className="text-sm">Recipient Email</Label>
              <Input
                id="recipient"
                type="email"
                placeholder="recipient@example.com"
                value={customEmail.recipient}
                onChange={(e) => setCustomEmail({ ...customEmail, recipient: e.target.value })}
                className="mt-1"
                data-testid="custom-email-recipient"
              />
            </div>
            <div>
              <Label htmlFor="subject" className="text-sm">Subject</Label>
              <Input
                id="subject"
                placeholder="Email subject"
                value={customEmail.subject}
                onChange={(e) => setCustomEmail({ ...customEmail, subject: e.target.value })}
                className="mt-1"
                data-testid="custom-email-subject"
              />
            </div>
            <div>
              <Label htmlFor="message" className="text-sm">Message (HTML supported)</Label>
              <Textarea
                id="message"
                placeholder="<p>Your message here...</p>"
                value={customEmail.message}
                onChange={(e) => setCustomEmail({ ...customEmail, message: e.target.value })}
                rows={5}
                className="mt-1 font-mono text-sm"
                data-testid="custom-email-message"
              />
            </div>
            <Button
              onClick={handleSendCustomEmail}
              disabled={sending}
              className="w-full"
              data-testid="send-custom-email-btn"
            >
              {sending ? 'Sending...' : (<><Send className="w-4 h-4 mr-2" />Send Custom Email</>)}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// ===== Main Component =====

const EmailAlertSettings = () => {
  const [activeTab, setActiveTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsData, setLogsData] = useState({ total: 0, page: 1, total_pages: 1 });
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilter, setLogsFilter] = useState({ alert_type: 'ALL', status: 'ALL' });
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const response = await axios.get(`${API}/email-rules`);
      setRules(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch email rules');
    } finally {
      setLoadingRules(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({ page: logsPage, page_size: 20 });
      if (logsFilter.alert_type && logsFilter.alert_type !== 'ALL') params.append('alert_type', logsFilter.alert_type);
      if (logsFilter.status && logsFilter.status !== 'ALL') params.append('status', logsFilter.status);
      const response = await axios.get(`${API}/email-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setLogsData(response.data);
    } catch (error) {
      toast.error('Failed to fetch email logs');
    } finally {
      setLoadingLogs(false);
    }
  }, [logsPage, logsFilter]);

  useEffect(() => {
    fetchRules();
    fetchUsers();
  }, [fetchRules, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, fetchLogs]);

  const handleToggleRule = async (ruleId, enabled) => {
    try {
      await axios.put(`${API}/email-rules/${ruleId}`, { is_enabled: enabled });
      setRules(prev => prev.map(r => r.id === ruleId ? { ...r, is_enabled: enabled } : r));
      toast.success(`Alert ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleUpdateRule = async (ruleId, data) => {
    try {
      await axios.put(`${API}/email-rules/${ruleId}`, data);
      await fetchRules();
      toast.success('Recipients updated successfully');
    } catch (error) {
      toast.error('Failed to update recipients');
    }
  };

  const handleTestRule = async (ruleId, testEmail) => {
    try {
      const response = await axios.post(`${API}/email-rules/test/${ruleId}`, { test_email: testEmail });
      if (response.data.status === 'success') {
        toast.success('Test email sent! Check your inbox.');
      } else if (response.data.status === 'disabled') {
        toast.warning('Email service not configured. Set up Gmail SMTP first.');
      } else {
        toast.error('Test email failed');
      }
    } catch (error) {
      toast.error('Failed to send test email', { description: error.response?.data?.detail || error.message });
    }
  };

  const tabs = [
    { id: 'rules', label: 'Alert Rules', icon: <Bell className="w-4 h-4" /> },
    { id: 'custom', label: 'Custom Alerts', icon: <Zap className="w-4 h-4" /> },
    { id: 'logs', label: 'Email Logs', icon: <Activity className="w-4 h-4" /> },
    { id: 'settings', label: 'Settings & Custom', icon: <Settings className="w-4 h-4" /> },
  ];

  // Stats for header
  const enabledCount = rules.filter(r => r.is_enabled).length;

  return (
    <div className="space-y-5" data-testid="email-alert-settings">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-50 to-indigo-50 p-5 rounded-lg border border-indigo-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-100 rounded-lg">
              <Mail className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Email Configuration</h2>
              <p className="text-sm text-slate-500">Manage alert rules, view logs, and configure email settings</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-xs">
              {enabledCount}/{rules.length} active
            </Badge>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg" data-testid="email-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'rules' && (
        <RulesTab
          rules={rules}
          users={users}
          onToggleRule={handleToggleRule}
          onUpdateRule={handleUpdateRule}
          onTestRule={handleTestRule}
        />
      )}

      {activeTab === 'custom' && <CustomAlertsTab users={users} />}

      {activeTab === 'logs' && (
        <LogsTab
          logs={logs}
          logsData={logsData}
          logsPage={logsPage}
          setLogsPage={setLogsPage}
          logsFilter={logsFilter}
          setLogsFilter={setLogsFilter}
          onRefresh={fetchLogs}
          loading={loadingLogs}
        />
      )}

      {activeTab === 'settings' && <SettingsTab />}
    </div>
  );
};

export default EmailAlertSettings;
